import { useEffect, useState } from 'react'
import {
  getPurchaseScratchAudit,
  getPurchaseScratchHistory,
  scratchError,
  type PurchaseScratchHistory as History,
} from '@/lib/purchase-scratch'
import styles from '@/components/PurchaseScratchCard/PurchaseScratchCard.module.css'

export function PurchaseScratchHistory({ campaignId }: { campaignId: number }) {
  const [page, setPage] = useState(1)
  const [type, setType] = useState('')
  const [payment, setPayment] = useState('')
  const [data, setData] = useState<History | null>(null)
  const [error, setError] = useState('')
  const [audit, setAudit] = useState<{
    id: number
    data: Record<string, unknown>
  } | null>(null)
  const [busy, setBusy] = useState(false)
  const [auditBusy, setAuditBusy] = useState(false)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const abort = new AbortController()
    const query = new URLSearchParams({ page: String(page) })
    if (type) query.set('type', type)
    if (payment) query.set('payment_id', payment)
    setBusy(true)
    setError('')
    void getPurchaseScratchHistory(campaignId, query, abort.signal)
      .then((result) => {
        if (!abort.signal.aborted) setData(result)
      })
      .catch((err: unknown) => {
        if (!abort.signal.aborted) setError(scratchError(err))
      })
      .finally(() => {
        if (!abort.signal.aborted) setBusy(false)
      })
    return () => abort.abort()
  }, [campaignId, page, type, payment, reload])
  async function inspect(id: number) {
    if (auditBusy) return
    setAuditBusy(true)
    setError('')
    try {
      setAudit({ id, data: await getPurchaseScratchAudit(campaignId, id) })
    } catch (err) {
      setError(scratchError(err))
    } finally {
      setAuditBusy(false)
    }
  }
  return (
    <section className={styles.section}>
      <span className={styles.eyebrow}>RASTREABILIDADE</span>
      <h3>Cada chance tem uma história</h3>
      <p className={styles.hint}>
        Consulte a compra de origem, o resultado, a revelação e o registro da configuração
        usada no sorteio.
      </p>
      <div className={styles.filters}>
        <label>
          Resultado
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value)
              setPage(1)
            }}
          >
            <option value="">Todos</option>
            <option value="coupon">Cupons</option>
            <option value="lucky_number">Números extras</option>
            <option value="none">Sem prêmio</option>
          </select>
        </label>
        <label>
          Compra nº
          <input
            type="number"
            min="1"
            value={payment}
            onChange={(e) => {
              setPayment(e.target.value)
              setPage(1)
            }}
          />
        </label>
        <button
          type="button"
          className={styles.quietButton}
          onClick={() => setReload((key) => key + 1)}
        >
          Atualizar
        </button>
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {busy ? (
        <p role="status">Carregando histórico…</p>
      ) : (
        <>
          {!data?.items.length && <p>Nenhuma tentativa encontrada.</p>}
          <div className={styles.historyList}>
            {data?.items.map((item) => (
              <article className={styles.historyItem} key={item.id}>
                <div>
                  <strong>
                    #{item.id} · {item.prize.prize_payload?.label}
                  </strong>
                  <span>
                    Compra #{item.payment_id} · Participante #{item.donor_id}
                  </span>
                  <small>
                    {new Date(item.created_at).toLocaleString('pt-BR')} ·{' '}
                    {item.revealed_at ? 'Revelada' : 'Ainda não revelada'} ·{' '}
                    {item.prize.status}
                  </small>
                </div>
                <button
                  type="button"
                  className={styles.quietButton}
                  disabled={auditBusy}
                  onClick={() => void inspect(item.id)}
                >
                  Ver auditoria
                </button>
              </article>
            ))}
          </div>
          {data && (
            <div className={styles.filters}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
              >
                Anterior
              </button>
              <span>
                Página {page} de {data.last_page} · {data.total} tentativas
              </span>
              <button
                type="button"
                disabled={page >= data.last_page}
                onClick={() => setPage((value) => value + 1)}
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
      {audit && (
        <div className={styles.audit}>
          <h4>Registro da tentativa #{audit.id}</h4>
          <p>
            O número sorteado deve pertencer ao intervalo cumulativo do resultado
            registrado. O hash SHA-256 identifica a configuração utilizada.
          </p>
          <pre>{JSON.stringify(audit.data, null, 2)}</pre>
          <button
            type="button"
            className={styles.quietButton}
            onClick={() => setAudit(null)}
          >
            Fechar registro
          </button>
        </div>
      )}
    </section>
  )
}
